import { Controller } from "@hotwired/stimulus"

// Typeahead controller for client selection
// Usage:
//   <div data-controller="typeahead" data-typeahead-url-value="/clients/search">
//     <input type="text" data-typeahead-target="input" data-action="input->typeahead#search focus->typeahead#search">
//     <input type="hidden" data-typeahead-target="hidden" name="client_id">
//     <ul data-typeahead-target="results"></ul>
//   </div>
export default class extends Controller {
  static targets = ["input", "hidden", "results"]
  static values = {
    url: String,
    minLength: { type: Number, default: 1 }
  }

  connect() {
    this.selectedIndex = -1
    this.resultsVisible = false
    this.debounceTimer = null

    // Close results when clicking outside
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this)
    document.addEventListener("click", this.boundCloseOnClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseOnClickOutside)
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  search() {
    const query = this.inputTarget.value.trim()

    // Clear hidden value when typing
    this.hiddenTarget.value = ""

    if (query.length < this.minLengthValue) {
      this.hideResults()
      return
    }

    // Debounce the search
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.fetchResults(query), 200)
  }

  async fetchResults(query) {
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("q", query)

      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Search failed")

      const results = await response.json()
      this.renderResults(results)
    } catch (error) {
      console.error("Typeahead search error:", error)
      this.hideResults()
    }
  }

  renderResults(results) {
    if (results.length === 0) {
      this.resultsTarget.innerHTML = `
        <li class="px-4 py-2 text-gray-500 text-sm">No clients found</li>
      `
      this.showResults()
      return
    }

    this.resultsTarget.innerHTML = results.map((item, index) => `
      <li class="px-4 py-2 cursor-pointer hover:bg-blue-50 text-sm border-b border-gray-100 last:border-0"
          data-action="click->typeahead#select mouseenter->typeahead#highlight"
          data-typeahead-id="${item.id}"
          data-typeahead-name="${this.escapeHtml(item.name)}"
          data-typeahead-index="${index}">
        <div class="font-medium text-gray-900">${this.escapeHtml(item.name)}</div>
        <div class="text-xs text-gray-500">${this.escapeHtml(item.email || "")}</div>
      </li>
    `).join("")

    this.selectedIndex = -1
    this.showResults()
  }

  select(event) {
    const li = event.currentTarget
    const id = li.dataset.typeaheadId
    const name = li.dataset.typeaheadName

    this.inputTarget.value = name
    this.hiddenTarget.value = id
    this.hideResults()
  }

  highlight(event) {
    const index = parseInt(event.currentTarget.dataset.typeaheadIndex, 10)
    this.updateHighlight(index)
  }

  keydown(event) {
    if (!this.resultsVisible) return

    const items = this.resultsTarget.querySelectorAll("[data-typeahead-id]")
    if (items.length === 0) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1)
        this.updateHighlight(this.selectedIndex)
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.updateHighlight(this.selectedIndex)
        break
      case "Enter":
        event.preventDefault()
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          items[this.selectedIndex].click()
        }
        break
      case "Escape":
        this.hideResults()
        break
    }
  }

  updateHighlight(index) {
    const items = this.resultsTarget.querySelectorAll("[data-typeahead-id]")
    items.forEach((item, i) => {
      if (i === index) {
        item.classList.add("bg-blue-50")
      } else {
        item.classList.remove("bg-blue-50")
      }
    })
    this.selectedIndex = index
  }

  showResults() {
    this.resultsTarget.classList.remove("hidden")
    this.resultsVisible = true
  }

  hideResults() {
    this.resultsTarget.classList.add("hidden")
    this.resultsVisible = false
    this.selectedIndex = -1
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
